import { mediaItems } from "@/mocks/media";
import { newsItems } from "@/mocks/news";
import type { MediaItem, NewsItem } from "@/types/media";

export function getMediaById(id: string): MediaItem | undefined {
  return mediaItems.find((item) => item.id === id);
}

export function getNewsById(id: string): NewsItem | undefined {
  return newsItems.find((item) => item.id === id);
}

export function getHotMedia(count = 6): MediaItem[] {
  return [...mediaItems].sort((a, b) => b.hot - a.hot).slice(0, count);
}

export function getLatestMedia(count = 6): MediaItem[] {
  return [...mediaItems].sort((a, b) => b.year - a.year).slice(0, count);
}

export function getRelatedMedia(item: MediaItem, count = 4): MediaItem[] {
  const sameType = mediaItems.filter(
    (m) => m.id !== item.id && m.type === item.type
  );
  const scored = sameType
    .map((m) => {
      let score = 0;
      if (m.director === item.director) score += 3;
      const sharedGenres = m.genres.filter((g) => item.genres.includes(g)).length;
      score += sharedGenres * 2;
      score += Math.max(0, 3 - Math.abs(m.year - item.year));
      return { m, score };
    })
    .sort((a, b) => b.score - a.score);
  return scored.slice(0, count).map((s) => s.m);
}

export function getAllRegions(): string[] {
  return Array.from(new Set(mediaItems.map((m) => m.region)));
}

export function getAllYears(): number[] {
  return Array.from(new Set(mediaItems.map((m) => m.year))).sort(
    (a, b) => b - a
  );
}

export function getAllGenres(): string[] {
  return Array.from(new Set(mediaItems.flatMap((m) => m.genres)));
}

export function getAllDirectors(): string[] {
  return Array.from(new Set(mediaItems.map((m) => m.director)));
}

export interface SearchResult {
  id: string;
  title: string;
  type: "电视剧" | "电影" | "资讯";
  poster: string;
  summary: string;
  year: number;
  rating: number;
  href: string;
}

export function buildSearchResults(keyword: string): SearchResult[] {
  const q = keyword.trim().toLowerCase();
  if (!q) return [];

  const mediaResults: SearchResult[] = mediaItems
    .filter((m) => {
      const haystack = [
        m.title,
        m.director,
        ...m.actors,
        ...m.genres,
        m.region,
        m.synopsis,
      ]
        .join(" ")
        .toLowerCase();
      return haystack.includes(q);
    })
    .map((m) => ({
      id: m.id,
      title: m.title,
      type: m.type,
      poster: m.poster,
      summary: m.synopsis,
      year: m.year,
      rating: m.rating,
      href: `/detail/${m.id}`,
    }));

  const newsResults: SearchResult[] = newsItems
    .filter((n) => {
      const haystack = [n.title, n.summary, n.tag].join(" ").toLowerCase();
      return haystack.includes(q);
    })
    .map((n) => ({
      id: n.id,
      title: n.title,
      type: "资讯" as const,
      poster: n.cover,
      summary: n.summary,
      year: Number(n.date.slice(0, 4)),
      rating: 0,
      href: `/detail/${n.id}`,
    }));

  return [...mediaResults, ...newsResults];
}